import React, { useState } from "react";
import {
  Box,
  Chip,
  DialogContent,
  IconButton,
  List,
  ListItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import Loading from "../../componentsV2/Loading";
import { useContactsV2 } from "../../queries";
import PrimaryButton from "../../componentsV2/buttons/PrimaryButton";
import Dialog from "./Dialog";
import { useCreateContact } from "../../mutations";
import { validateEmailAddress } from "../../utils/helpers";
import { Contact } from "../../types";
import { Close } from "@mui/icons-material";

type AddEventGuestProps = {
  open: boolean;
  onClose?: () => void;
  onConfirm: (contact: Contact) => void;
};

export function AddEventGuestDialog({
  open,
  onClose,
  onConfirm,
}: AddEventGuestProps) {
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");

  React.useEffect(() => {
    if (open) {
      setEmail("");
      setFirstName("");
      setLastName("");
    }
  }, [open]);

  const createContact = useCreateContact();

  const { data, loading, error } = useContactsV2(5, 0, {
    filter: { firstName, lastName, email },
  });

  const { data: emailSearch } = useContactsV2(5, 0, { filter: { email } });

  const unsubscribed = emailSearch?.data.some(
    (contact: Contact) =>
      contact.email.trim().toLowerCase() === email.trim().toLowerCase() &&
      contact.unsubscribed,
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={"sm"}
      title="Add Event Guest"
      customHeader={
        <Box
          sx={{
            backgroundColor: "#004c6d",
            color: "white",
            px: 3,
            py: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              Add Event Guest
            </Typography>
          </Box>
          <IconButton onClick={onClose} sx={{ color: "white" }}>
            <Close />
          </IconButton>
        </Box>
      }
    >
      <DialogContent>
        <Typography variant="body2" color="text.secondary">
          Enter an email address, first name, or last name to search for an
          existing contact. You can also click "Create New Contact" to add
          someone new.
        </Typography>
        <Stack spacing={2} sx={{ p: 2, height: "600px" }}>
          <TextField
            autoFocus
            fullWidth
            label="Email"
            required
            value={email}
            error={(email && !validateEmailAddress(email)) || unsubscribed}
            helperText={
              email && !validateEmailAddress(email)
                ? "Must be a valid email"
                : unsubscribed
                  ? "Contact already exists and is unsubscribed"
                  : ""
            }
            onChange={(e) => setEmail(e.target.value)}
          />
          <TextField
            fullWidth
            label="First Name"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
          />
          <TextField
            fullWidth
            label="Last Name"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
          />
          <PrimaryButton
            disabled={!validateEmailAddress(email) || unsubscribed}
            onClick={() => {
              createContact({
                email,
                firstName,
                lastName,
                unsubscribed: false,
              }).then((contact: Contact) => {
                onConfirm(contact);
              });
            }}
          >
            Create New Contact & Add as Guest
          </PrimaryButton>
          {error || (!firstName && !lastName && !email) ? null : loading ? (
            <Loading />
          ) : (
            <List>
              {data.data.map((contact: Contact) => (
                <ListItem
                  key={contact.id}
                  secondaryAction={
                    <Stack direction="row" spacing={1} alignItems="center">
                      {contact.unsubscribed && (
                        <Chip size="small" label="Unsubscribed" />
                      )}
                      <PrimaryButton
                        disabled={contact.unsubscribed}
                        onClick={() => onConfirm(contact)}
                      >
                        Add Guest
                      </PrimaryButton>
                    </Stack>
                  }
                >
                  <Stack>
                    <Typography>{`${contact.firstName} ${contact.lastName}`}</Typography>
                    <Typography>{contact.email}</Typography>
                  </Stack>
                </ListItem>
              ))}
              {data.data.length === 0 && (
                <Typography align="center">No results</Typography>
              )}
            </List>
          )}
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
